/**
 * Boards API service - handles board API calls
 */

import { apiClient } from './client';

// Types
export interface BoardColumn {
  id: string;
  name: string;
  position: number;
  wipLimit?: number;
  color?: string;
}

export interface Board {
  id: string;
  projectId: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  columns: BoardColumn[];
  isDefault: boolean;
  /** Archived boards are hidden from project views; their tasks are kept. */
  isArchived: boolean;
  archivedAt: string | null;
  viewSettings: BoardViewSettings;
  createdAt: string;
  updatedAt: string;
  taskCount?: number;
  columnTaskCounts?: Record<string, number>;
}

export interface BoardViewSettings {
  showLabels: boolean;
  showAssignee: boolean;
  showDueDate: boolean;
  showPriority: boolean;
  showSubtaskProgress: boolean;
  showAttachments: boolean;
  compactMode: boolean;
}

export interface BoardDetail extends Board {
  taskCount: number;
  columnTaskCounts: Record<string, number>;
}

export interface BoardListResponse {
  items: Board[];
  total: number;
}

export interface CreateBoardRequest {
  projectId: string;
  name: string;
  description?: string;
  columns?: Partial<BoardColumn>[];
  icon?: string;
  color?: string;
}

export interface UpdateBoardRequest {
  name?: string;
  description?: string;
  color?: string;
  icon?: string;
  isDefault?: boolean;
  viewSettings?: Partial<BoardViewSettings>;
}

export interface ColumnCreateRequest {
  name: string;
  position?: number;
  wipLimit?: number;
  color?: string;
}

export interface ColumnUpdateRequest {
  name?: string;
  wipLimit?: number;
  color?: string;
}

export interface ColumnsUpdateRequest {
  columns: BoardColumn[];
}

export interface BoardViewData {
  board: Board;
  tasks: any[];
  [key: string]: any;
}

/**
 * Boards API methods
 */
export const boardsApi = {
  /**
   * List boards in a project
   */
  async list(projectId: string, includeArchived = false): Promise<BoardListResponse> {
    const response = await apiClient.get<any>('/boards', {
      params: { project_id: projectId, include_archived: includeArchived },
    });
    return {
      items: response.data.items.map(transformBoard),
      total: response.data.total,
    };
  },

  /**
   * Get board by ID
   */
  async get(boardId: string): Promise<BoardDetail> {
    const response = await apiClient.get<any>(`/boards/${boardId}`);
    return transformBoardDetail(response.data);
  },

  /**
   * Create a new board
   */
  async create(data: CreateBoardRequest): Promise<Board> {
    const response = await apiClient.post<any>('/boards', {
      project_id: data.projectId,
      name: data.name,
      description: data.description,
      icon: data.icon,
      color: data.color,
      columns: data.columns?.map((col) => ({
        name: col.name,
        position: col.position,
        wip_limit: col.wipLimit,
        color: col.color,
      })),
    });
    return transformBoard(response.data);
  },

  /**
   * Update a board
   */
  async update(boardId: string, data: UpdateBoardRequest): Promise<Board> {
    const payload: any = {};
    if (data.name !== undefined) payload.name = data.name;
    if (data.description !== undefined) payload.description = data.description;
    if (data.color !== undefined) payload.color = data.color;
    if (data.icon !== undefined) payload.icon = data.icon;
    if (data.isDefault !== undefined) payload.is_default = data.isDefault;
    if (data.viewSettings !== undefined) {
      payload.view_settings = transformViewSettingsToSnakeCase(data.viewSettings);
    }

    const response = await apiClient.patch<any>(`/boards/${boardId}`, payload);
    return transformBoard(response.data);
  },

  /**
   * Delete a board
   */
  async delete(boardId: string): Promise<void> {
    await apiClient.delete(`/boards/${boardId}`);
  },

  /** Hide a board from project views; tasks are kept and it can be restored. */
  async archive(boardId: string): Promise<Board> {
    const response = await apiClient.post<Record<string, unknown>>(`/boards/${boardId}/archive`);
    return transformBoard(response.data);
  },

  /** Bring an archived board back into project views. */
  async restore(boardId: string): Promise<Board> {
    const response = await apiClient.post<Record<string, unknown>>(`/boards/${boardId}/restore`);
    return transformBoard(response.data);
  },

  /**
   * Update all columns for a board
   */
  async updateColumns(boardId: string, columns: BoardColumn[]): Promise<Board> {
    const response = await apiClient.put<any>(`/boards/${boardId}/columns`, {
      columns: columns.map((col) => ({
        id: col.id,
        name: col.name,
        position: col.position,
        wip_limit: col.wipLimit,
        color: col.color,
      })),
    });
    return transformBoard(response.data);
  },

  /**
   * Add a column to a board
   */
  async addColumn(boardId: string, data: ColumnCreateRequest): Promise<Board> {
    const response = await apiClient.post<any>(`/boards/${boardId}/columns`, {
      name: data.name,
      position: data.position,
      wip_limit: data.wipLimit,
      color: data.color,
    });
    return transformBoard(response.data);
  },

  /**
   * Update a specific column
   */
  async updateColumn(
    boardId: string,
    columnId: string,
    data: ColumnUpdateRequest
  ): Promise<Board> {
    const payload: any = {};
    if (data.name !== undefined) payload.name = data.name;
    if (data.wipLimit !== undefined) payload.wip_limit = data.wipLimit;
    if (data.color !== undefined) payload.color = data.color;

    const response = await apiClient.patch<any>(
      `/boards/${boardId}/columns/${columnId}`,
      payload
    );
    return transformBoard(response.data);
  },

  /**
   * Delete a column
   */
  async deleteColumn(boardId: string, columnId: string): Promise<Board> {
    const response = await apiClient.delete<any>(
      `/boards/${boardId}/columns/${columnId}`
    );
    return transformBoard(response.data);
  },

  /**
   * Get board data for a specific view type
   */
  async getViewData(
    boardId: string,
    viewType: 'kanban' | 'list' | 'timeline' | 'calendar' | 'table'
  ): Promise<BoardViewData> {
    const response = await apiClient.get<any>(
      `/boards/${boardId}/view/${viewType}`
    );
    return {
      board: transformBoard(response.data.board),
      tasks: response.data.tasks || [],
      ...response.data,
    };
  },
};

// Transform functions
// Note: Backend uses alias_generator=to_camel, so API responses are camelCase
function transformBoard(data: any): Board {
  return {
    id: data.id,
    projectId: data.projectId ?? data.project_id,
    name: data.name,
    description: data.description,
    color: data.color,
    icon: data.icon,
    columns: (data.columns || []).map(transformColumn),
    isDefault: data.isDefault ?? data.is_default,
    isArchived: data.isArchived ?? data.is_archived ?? false,
    archivedAt: data.archivedAt ?? data.archived_at ?? null,
    viewSettings: transformViewSettings(data.viewSettings ?? data.view_settings ?? {}),
    createdAt: data.createdAt ?? data.created_at,
    updatedAt: data.updatedAt ?? data.updated_at,
    taskCount: data.taskCount ?? data.task_count,
    columnTaskCounts: data.columnTaskCounts ?? data.column_task_counts,
  };
}

function transformBoardDetail(data: any): BoardDetail {
  return {
    ...transformBoard(data),
    taskCount: data.taskCount ?? data.task_count ?? 0,
    columnTaskCounts: data.columnTaskCounts ?? data.column_task_counts ?? {},
  };
}

function transformColumn(data: any): BoardColumn {
  return {
    id: data.id,
    name: data.name,
    position: data.position,
    wipLimit: data.wipLimit ?? data.wip_limit,
    color: data.color,
  };
}

function transformViewSettings(data: any): BoardViewSettings {
  return {
    showLabels: data.showLabels ?? data.show_labels ?? true,
    showAssignee: data.showAssignee ?? data.show_assignee ?? true,
    showDueDate: data.showDueDate ?? data.show_due_date ?? true,
    showPriority: data.showPriority ?? data.show_priority ?? true,
    showSubtaskProgress: data.showSubtaskProgress ?? data.show_subtask_progress ?? true,
    showAttachments: data.showAttachments ?? data.show_attachments ?? true,
    compactMode: data.compactMode ?? data.compact_mode ?? false,
  };
}

function transformViewSettingsToSnakeCase(
  settings: Partial<BoardViewSettings>
): any {
  const result: any = {};
  if (settings.showLabels !== undefined) result.show_labels = settings.showLabels;
  if (settings.showAssignee !== undefined) result.show_assignee = settings.showAssignee;
  if (settings.showDueDate !== undefined) result.show_due_date = settings.showDueDate;
  if (settings.showPriority !== undefined) result.show_priority = settings.showPriority;
  if (settings.showSubtaskProgress !== undefined)
    result.show_subtask_progress = settings.showSubtaskProgress;
  if (settings.showAttachments !== undefined) result.show_attachments = settings.showAttachments;
  if (settings.compactMode !== undefined) result.compact_mode = settings.compactMode;
  return result;
}

export default boardsApi;
